import cuid from 'cuid';
import MutationListPosition from '@/constants/swr/MutationListPosition';

/**
 * This is used with mutating SWR data. It provides a way for
 * adding an item to the start or end of a list, while also updating
 * the total hits. When passed in `page` and `pageSize`, it has the
 * ability to know if the list should be updated or not. If you are
 * viewing page 3 of 5, and a new item is added to the end of the list,
 * page 3 will not be mutated.
 *
 * @example
 * mutate(
 *   key,
 *   (currentData: GetCampaignEventsData) =>
 *     addItemToListMutation<DataKey, IdKey, CampaignEvent>({
 *       dataKey: 'ticketedEvents',
 *       idKey: 'ticketedEventId',
 *       currentData,
 *       item: newCampaignEvent
 *     }),
 *   false
 * )
 *
 * @param dataKey key for the data on currentData
 * @param idKey key for the ID of the item being added
 * @param item the new item being added to the list
 * @param currentData mutation data
 * @param options
 */
export default function addItemToListMutation<
  DataKey extends string,
  IdKey extends string,
  Item extends {
    [key in IdKey]?: string;
  },
>({
  dataKey,
  idKey,
  item,
  currentData,
  options: { page, pageSize, position = MutationListPosition.START } = {},
}: {
  currentData: {
    [key in DataKey]: Item[];
  } & {
    totalHits: number;
  };
  dataKey: DataKey;
  idKey: IdKey;
  item: Item;
  options?: {
    page?: number;
    pageSize?: number;
    position?: MutationListPosition;
  };
}) {
  const items = currentData[dataKey];
  const { totalHits = 0 } = currentData;
  const updatedTotalHits = totalHits + 1;

  // We only want to mutate the items list if we are on page 1. New items are
  // added to the top of the list. On any other page, we'll leave items as-is
  // but we do want to update the total hits.
  if (typeof page === 'number' && page !== 1) {
    return {
      ...currentData,
      totalHits: updatedTotalHits,
    };
  }

  // If we're on the first page and we have a full list, we want to show the
  // new item at the top. But we want to remove the last item so that we can
  // make sure that we still render the number of items that match the page
  // size.
  if (
    page === 1 &&
    items.length === pageSize &&
    position === MutationListPosition.START
  ) {
    items.pop();
  }

  const newItem = {
    ...item,
    // use cuid to temporarily add an id, potentially preventing
    // the list from throwing a console error when it renders
    // the new item.
    [idKey]: item[idKey] || cuid(),
  };
  return {
    ...currentData,
    [dataKey]:
      position === MutationListPosition.START
        ? [newItem, ...(items || [])]
        : [...(items || []), newItem],
    totalHits: updatedTotalHits,
  };
}
